import { Mui, MuiIcons } from '@leap4/frontend';
import React from 'react';

import type { ReportConstraintOperator } from '../report.interface';
import { EmptyRow, rowSx } from './ruleRow';

const { Box, Button, IconButton, MenuItem, TextField, Tooltip } = Mui;
const { Add, Close } = MuiIcons;

interface Constraint {
    id: string;
    metric: string;
    operator: ReportConstraintOperator;
    value: number | '';
}

interface ConstraintsProps {
    /** Currently selected metrics, keyed by projected column key. */
    metricOptions: { key: string; value: string }[];
    constraintsData: Constraint[];
    onChange: (id: string, field: string, value: any) => void;
    onAddConstraint: () => void;
    onDeleteConstraint: (id: string) => void;
}

const operatorOptions: { key: ReportConstraintOperator; value: string; label: string }[] = [
    { key: 'gt', value: '>', label: 'greater than' },
    { key: 'gte', value: '≥', label: 'at least' },
    { key: 'lt', value: '<', label: 'less than' },
    { key: 'lte', value: '≤', label: 'at most' },
    { key: 'eq', value: '=', label: 'equal to' },
    { key: 'neq', value: '≠', label: 'not equal to' },
];

const constraintRowSx = {
    ...rowSx,
    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 80px 104px auto' },
};

const ConstraintItem = ({ constraint, metricOptions, onChange, onDeleteConstraint }: { constraint: Constraint; metricOptions: ConstraintsProps['metricOptions']; onChange: ConstraintsProps['onChange']; onDeleteConstraint: ConstraintsProps['onDeleteConstraint'] }) => {
    // A metric deselected after its constraint was added would be rejected by the backend, so flag it here.
    const metricIsSelected = metricOptions.some((o) => o.key === constraint.metric);
    const metricMissing = !!constraint.metric && !metricIsSelected;

    return (
        <Box sx={constraintRowSx}>
            <TextField
                select
                size="small"
                fullWidth
                label="Metric"
                value={metricIsSelected ? constraint.metric : ''}
                onChange={(e) => onChange(constraint.id, 'metric', e.target.value)}
                error={metricMissing}
                helperText={metricMissing ? 'No longer selected' : undefined}
            >
                {metricOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        {option.value}
                    </MenuItem>
                ))}
            </TextField>

            <TextField select size="small" fullWidth label="Is" value={constraint.operator} onChange={(e) => onChange(constraint.id, 'operator', e.target.value)}>
                {operatorOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        <Box component="span" sx={{ minWidth: 20, fontWeight: 600 }}>{option.value}</Box>
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8125rem', ml: 1, '.MuiSelect-select &': { display: 'none' } }}>{option.label}</Box>
                    </MenuItem>
                ))}
            </TextField>

            <TextField
                size="small"
                type="number"
                fullWidth
                label="Value"
                value={constraint.value}
                onChange={(e) => onChange(constraint.id, 'value', e.target.value === '' ? '' : Number(e.target.value))}
                slotProps={{ htmlInput: { step: 'any' } }}
            />

            <Tooltip title="Remove constraint">
                <IconButton size="small" onClick={() => onDeleteConstraint(constraint.id)} aria-label="Remove constraint">
                    <Close fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const Constraints: React.FC<ConstraintsProps> = ({ metricOptions, constraintsData, onChange, onAddConstraint, onDeleteConstraint }) => {
    const addButton = (
        <Button size="small" startIcon={<Add />} onClick={onAddConstraint} disabled={metricOptions.length === 0}>
            Add constraint
        </Button>
    );
    return constraintsData.length === 0 ? (
        <EmptyRow text={metricOptions.length === 0 ? 'Pick a metric first.' : 'No constraints.'} action={addButton} />
    ) : (
        <Box sx={{ display: 'grid', gap: 1 }}>
            {constraintsData.map((constraint) => (
                <ConstraintItem key={constraint.id} constraint={constraint} metricOptions={metricOptions} onChange={onChange} onDeleteConstraint={onDeleteConstraint} />
            ))}
            <Box>{addButton}</Box>
        </Box>
    );
};

export default Constraints;
